import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, LogIn, LogOut, MapPin, Briefcase, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Module-level: keeps a stable component identity so textareas don't remount per keystroke
const ReportField = ({ label, name, testid, value, onChange }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Textarea rows={2} data-testid={testid} value={value} onChange={onChange} />
  </div>
);
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

export default function ClockInOut() {
  const queryClient = useQueryClient();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [loc, setLoc] = useState(null);
  const [photo, setPhoto] = useState(null); // {blob, url}
  const [cameraOn, setCameraOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: today, refetch } = useQuery({
    queryKey: ["staff-today"],
    queryFn: async () => (await api.get("/staff/today")).data,
  });
  const { data: history = [] } = useQuery({
    queryKey: ["staff-history"],
    queryFn: async () => (await api.get("/staff/history")).data,
  });
  const { data: absenceWarning } = useQuery({
    queryKey: ["absence-warning"],
    queryFn: async () => (await api.get("/staff/absence-warning")).data,
  });

  const [onDutyOpen, setOnDutyOpen] = useState(false);
  const [onDutyForm, setOnDutyForm] = useState({ destination: "", purpose: "" });
  const [report, setReport] = useState({ allocated_classes: "0", conducted_classes: "0", missed_reason: "", problems: "", academic_activity: "", admin_assigned: "", admin_done: "", advancement: "", improvement: "" });
  const [reportSaving, setReportSaving] = useState(false);

  const { data: todayReport, refetch: refetchReport } = useQuery({
    queryKey: ["daily-report-today"],
    queryFn: async () => (await api.get("/staff/daily-report/today")).data,
  });
  const { data: yearly } = useQuery({
    queryKey: ["staff-yearly"],
    queryFn: async () => (await api.get("/staff/my-yearly", { params: { year: new Date().getFullYear() } })).data,
  });
  const reportToday = !!todayReport;
  const reportRequired = today?.settings?.daily_report_required !== false;

  useEffect(() => {
    if (todayReport) {
      setReport({
        allocated_classes: String(todayReport.allocated_classes),
        conducted_classes: String(todayReport.conducted_classes),
        missed_reason: todayReport.missed_reason || "", problems: todayReport.problems || "",
        academic_activity: todayReport.academic_activity || "", admin_assigned: todayReport.admin_assigned || "",
        admin_done: todayReport.admin_done || "", advancement: todayReport.advancement || "",
        improvement: todayReport.improvement || "",
      });
    }
  }, [todayReport]);

  const submitOnDuty = async () => {
    setBusy(true);
    try {
      await api.post("/staff/on-duty", onDutyForm);
      toast.success("Marked as On Duty");
      setOnDutyOpen(false);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["staff-history"] });
      queryClient.invalidateQueries({ queryKey: ["staff-yearly"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const saveReport = async () => {
    setReportSaving(true);
    try {
      await api.post("/staff/daily-report", {
        ...report,
        allocated_classes: Number(report.allocated_classes),
        conducted_classes: Number(report.conducted_classes),
      });
      toast.success("Daily report submitted — clock-out enabled");
      refetchReport();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReportSaving(false); }
  };

  const CountSelect = ({ label, value, onChange, testid }) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={String(value)} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {Array.from({ length: 13 }, (_, i) => <SelectItem key={i} value={String(i)}>{i}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const record = today?.record;
  const clockedIn = !!record;
  const clockedOut = !!record?.clock_out;

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const getLocation = () =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => reject(new Error("Location access denied — enable GPS/location and retry")),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });

  const startCapture = async () => {
    setBusy(true);
    try {
      const position = await getLocation();
      setLoc(position);
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      setCameraOn(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch (e) {
      toast.error(e.message || "Camera/location unavailable");
    } finally {
      setBusy(false);
    }
  };

  const snap = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      setPhoto({ blob, url: URL.createObjectURL(blob) });
    }, "image/jpeg", 0.85);
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  };

  const submit = async () => {
    if (!loc || !photo) {
      toast.error("Location and selfie are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("latitude", loc.lat);
      fd.append("longitude", loc.lng);
      fd.append("selfie", photo.blob, "selfie.jpg");
      const endpoint = clockedIn ? "/staff/clock-out" : "/staff/clock-in";
      const { data } = await api.post(endpoint, fd);
      toast.success(clockedIn ? `Clocked out at ${data.clock_out}` : `Clocked in at ${data.clock_in}`);
      stopCamera();
      setPhoto(null);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["staff-history"] });
      queryClient.invalidateQueries({ queryKey: ["absence-warning"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    stopCamera();
    setPhoto(null);
    setLoc(null);
  };

  return (
    <Layout title="Clock In / Clock Out">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="clock-card">
          <p className="text-sm text-slate-500">Today · <span className="font-data">{today?.date}</span></p>
          {absenceWarning?.absent_date && (
            <div className="mt-4 bg-amber-50 border border-amber-300 rounded-md px-4 py-3 flex flex-wrap items-center justify-between gap-3"
              data-testid="absence-warning-banner">
              <p className="text-sm text-amber-800">
                You were absent on <span className="font-data font-medium">{absenceWarning.absent_date}</span> and no leave application was found.
                Submit one — otherwise clock-in stays blocked.
              </p>
              <Button size="sm" variant="outline" data-testid="absence-warning-apply-btn" asChild
                className="text-amber-800 border-amber-300 hover:bg-amber-100">
                <Link to="/teacher/leave">Apply for leave</Link>
              </Button>
            </div>
          )}
          {!today?.geofence_configured && (
            <p className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="geofence-missing">
              Geofence is not configured for your department yet. Ask the admin to set it up.
            </p>
          )}
          <div className="mt-4 flex items-center gap-6">
            <div>
              <p className="text-xs text-slate-400">Clock in</p>
              <p className="font-data text-2xl font-semibold text-slate-950" data-testid="clock-in-time">{record?.clock_in || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Clock out</p>
              <p className="font-data text-2xl font-semibold text-slate-950" data-testid="clock-out-time">{record?.clock_out || "—"}</p>
            </div>
          </div>
          <div className="mt-3 flex gap-2 flex-wrap">
            {record?.beyond && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid="beyond-badge">Beyond time — counted absent</Badge>}
            {record?.not_punctual && !record?.beyond && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200" data-testid="not-punctual-badge">Not punctual</Badge>}
            {record?.late && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="late-badge">Late arrival</Badge>}
            {record?.early_departure && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="early-badge">Early departure</Badge>}
            {record && !record.late && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">On time</Badge>}
          </div>

          {record?.status === "on_duty" && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md px-4 py-3" data-testid="on-duty-info">
              <p className="text-sm font-semibold text-[#002FA7]">On Duty today</p>
              <p className="text-sm text-slate-600 mt-1"><span className="text-slate-400">Destination:</span> {record.destination}</p>
              <p className="text-sm text-slate-600"><span className="text-slate-400">Purpose:</span> {record.purpose}</p>
            </div>
          )}
          {!record && !cameraOn && !onDutyOpen && (
            <div className="mt-6 flex gap-2 flex-wrap">
              <Button onClick={startCapture} disabled={busy || !today?.geofence_configured} data-testid="start-clock-btn"
                className="bg-slate-950 hover:bg-slate-800 text-white">
                <LogIn className="h-4 w-4" /> {busy ? "Getting location…" : "Clock in"}
              </Button>
              <Button variant="outline" data-testid="on-duty-open-btn" onClick={() => setOnDutyOpen(true)}>
                <Briefcase className="h-4 w-4" /> Mark On Duty
              </Button>
            </div>
          )}
          {clockedIn && !clockedOut && !cameraOn && (
            <div className="mt-6">
              <Button onClick={startCapture} disabled={busy || (reportRequired && !reportToday)} data-testid="start-clock-btn"
                className="bg-slate-950 hover:bg-slate-800 text-white">
                <LogOut className="h-4 w-4" /> {busy ? "Getting location…" : "Clock out"}
              </Button>
              {reportRequired && !reportToday && (
                <p className="text-xs text-amber-700 mt-2" data-testid="clockout-blocked-hint">
                  Submit your daily report below to enable clock-out.
                </p>
              )}
            </div>
          )}
          {onDutyOpen && !record && (
            <div className="mt-6 space-y-4 border border-slate-200 rounded-md p-4" data-testid="on-duty-form">
              <p className="text-sm font-medium text-slate-950">Mark today as On Duty — no geofence or selfie needed.</p>
              <div className="space-y-1.5"><Label>Destination</Label>
                <Input data-testid="on-duty-destination" value={onDutyForm.destination}
                  onChange={(e) => setOnDutyForm({ ...onDutyForm, destination: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Purpose</Label>
                <Textarea rows={2} data-testid="on-duty-purpose" value={onDutyForm.purpose}
                  onChange={(e) => setOnDutyForm({ ...onDutyForm, purpose: e.target.value })} /></div>
              <div className="flex gap-2">
                <Button onClick={submitOnDuty} disabled={busy} data-testid="on-duty-submit-btn"
                  className="bg-slate-950 hover:bg-slate-800 text-white">{busy ? "Saving…" : "Confirm On Duty"}</Button>
                <Button variant="ghost" onClick={() => setOnDutyOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {cameraOn && (
            <div className="mt-6 space-y-4" data-testid="capture-panel">
              {loc && (
                <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="location-readout">
                  <MapPin className="h-3.5 w-3.5" /> {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}
                </p>
              )}
              {!photo ? (
                <>
                  <video ref={videoRef} autoPlay playsInline muted data-testid="selfie-video"
                    className="w-full max-w-sm rounded-md border border-slate-200 bg-slate-950" />
                  <div className="flex gap-2">
                    <Button onClick={snap} variant="outline" data-testid="capture-selfie-btn">
                      <Camera className="h-4 w-4" /> Capture selfie
                    </Button>
                    <Button onClick={cancel} variant="ghost" data-testid="cancel-capture-btn">Cancel</Button>
                  </div>
                </>
              ) : (
                <>
                  <img src={photo.url} alt="Selfie preview" data-testid="selfie-preview"
                    className="w-full max-w-sm rounded-md border border-slate-200" />
                  <div className="flex gap-2">
                    <Button onClick={submit} disabled={busy} data-testid="submit-clock-btn"
                      className="bg-slate-950 hover:bg-slate-800 text-white">
                      {busy ? "Submitting…" : clockedIn ? "Confirm clock out" : "Confirm clock in"}
                    </Button>
                    <Button onClick={() => setPhoto(null)} variant="outline" data-testid="retake-selfie-btn">Retake</Button>
                    <Button onClick={cancel} variant="ghost">Cancel</Button>
                  </div>
                </>
              )}
            </div>
          )}
          {clockedOut && (
            <p className="mt-6 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="day-complete">
              Attendance complete for today. See you tomorrow!
            </p>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md" data-testid="staff-history-card">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">My history</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>In</TableHead>
                <TableHead>Out</TableHead>
                <TableHead>Late</TableHead>
                <TableHead>Early dep.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8" data-testid="staff-history-empty">
                  No clock-ins yet.
                </TableCell></TableRow>
              )}
              {history.map((r) => (
                <TableRow key={r.id} data-testid={`staff-history-${r.id}`}>
                  <TableCell className="font-data">{r.date}</TableCell>
                  <TableCell className="font-data">{r.clock_in}</TableCell>
                  <TableCell className="font-data">{r.clock_out || "—"}</TableCell>
                  <TableCell>{r.late ? <span className="text-amber-700 font-medium">Yes</span> : "No"}</TableCell>
                  <TableCell>{r.early_departure ? <span className="text-amber-700 font-medium">Yes</span> : "No"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {reportRequired && (
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="daily-report-card">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Daily report</h2>
            {reportToday && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="report-submitted-badge">Submitted</Badge>
            )}
          </div>
          <p className="text-xs text-slate-400 mb-4">Required before clock-out. Editable until you clock out.</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <CountSelect label="Number of allocated classes" value={report.allocated_classes} testid="report-allocated"
                onChange={(v) => setReport({ ...report, allocated_classes: v })} />
              <CountSelect label="Number of classes conducted" value={report.conducted_classes} testid="report-conducted"
                onChange={(v) => setReport({ ...report, conducted_classes: v })} />
            </div>
            {Number(report.conducted_classes) < Number(report.allocated_classes) && (
              <ReportField label="Reason for missed class(es) — write details (semester, period)" name="missed_reason" testid="report-missed"
                value={report.missed_reason} onChange={(e) => setReport({ ...report, missed_reason: e.target.value })} />
            )}
            <ReportField label="Problems faced" name="problems" testid="report-problems"
              value={report.problems} onChange={(e) => setReport({ ...report, problems: e.target.value })} />
            <ReportField label="Academic activity" name="academic_activity" testid="report-academic"
              value={report.academic_activity} onChange={(e) => setReport({ ...report, academic_activity: e.target.value })} />
            <ReportField label="Administrative task assigned" name="admin_assigned" testid="report-admin-assigned"
              value={report.admin_assigned} onChange={(e) => setReport({ ...report, admin_assigned: e.target.value })} />
            <ReportField label="Administrative task done" name="admin_done" testid="report-admin-done"
              value={report.admin_done} onChange={(e) => setReport({ ...report, admin_done: e.target.value })} />
            <ReportField label="Self academic advancement (with time spent)" name="advancement" testid="report-advancement"
              value={report.advancement} onChange={(e) => setReport({ ...report, advancement: e.target.value })} />
            <ReportField label="Any substantial improvement suggested/done" name="improvement" testid="report-improvement"
              value={report.improvement} onChange={(e) => setReport({ ...report, improvement: e.target.value })} />
            <Button onClick={saveReport} disabled={reportSaving} data-testid="report-submit-btn"
              className="w-full bg-slate-950 hover:bg-slate-800 text-white">
              <FileText className="h-4 w-4" /> {reportSaving ? "Saving…" : reportToday ? "Update daily report" : "Submit daily report"}
            </Button>
          </div>
        </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md" data-testid="yearly-summary-card">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Yearly summary — {yearly?.year}</h2>
            <p className="text-xs text-slate-400 mt-0.5">Cumulative January to December (teacher attendance counted from 7 September 2026). Leave Without Pay adjusts per month, no carry forward.</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Working</TableHead>
                <TableHead className="text-right">Present</TableHead>
                <TableHead className="text-right">Not punctual</TableHead>
                <TableHead className="text-right">Late</TableHead>
                <TableHead className="text-right">On duty</TableHead>
                <TableHead className="text-right">Absent</TableHead>
                <TableHead className="text-right">LWP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!yearly || yearly.months.length === 0) && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8" data-testid="yearly-empty">No data yet.</TableCell></TableRow>
              )}
              {yearly?.months?.map((m) => (
                <TableRow key={m.month} data-testid={`yearly-month-${m.month}`}>
                  <TableCell className="font-medium">{m.month_name}</TableCell>
                  <TableCell className="text-right font-data">{m.working}</TableCell>
                  <TableCell className="text-right font-data text-emerald-700">{m.present}</TableCell>
                  <TableCell className="text-right font-data text-orange-700">{m.not_punctual}</TableCell>
                  <TableCell className="text-right font-data text-amber-700">{m.late}</TableCell>
                  <TableCell className="text-right font-data text-[#002FA7]">{m.on_duty}</TableCell>
                  <TableCell className="text-right font-data text-red-700">{m.absent}</TableCell>
                  <TableCell className="text-right font-data">{m.lwp}</TableCell>
                </TableRow>
              ))}
              {yearly?.totals && yearly.months.length > 0 && (
                <TableRow className="bg-slate-50 font-semibold" data-testid="yearly-totals">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right font-data">{yearly.totals.working}</TableCell>
                  <TableCell className="text-right font-data">{yearly.totals.present}</TableCell>
                  <TableCell className="text-right font-data">{yearly.totals.not_punctual}</TableCell>
                  <TableCell className="text-right font-data">{yearly.totals.late}</TableCell>
                  <TableCell className="text-right font-data">{yearly.totals.on_duty}</TableCell>
                  <TableCell className="text-right font-data">{yearly.totals.absent}</TableCell>
                  <TableCell className="text-right font-data">{yearly.totals.lwp}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
